import React, { useEffect, useRef, useState } from 'react';
import { computeSessionTimerPhase } from '../../utils/sessionTimer';
import { useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getSessionConfig, sendChatMessage, endSession, getSessionMessages } from '../../services/api';
import { VoiceInput } from '../../services/voice/voiceInput';
import { VoiceOutputTTS, unlockAudio } from '../../services/voice/voiceOutputTTS';
import LanternMark from '../../components/LanternMark';
import { accessibilityStyles, shouldReduceMotion } from '../../utils/accessibility';
import { thinkingMessage } from '../../utils/thinkingProgress';
import ReportButton from '../../components/chat/ReportButton';

// ─── Brand tokens (child interface) ───────────────────────────────────────────

const C = {
  bg: '#FFFDF7',
  text: '#1A1F36',
  textMuted: '#5A6478',
  textSubtle: '#8A93A6',
  accent: '#F59E0B',
  accentDark: '#D97706',
  accentSoft: '#FEF3C7',
  green: '#10B981',
  card: '#FFFFFF',
  border: '#F0E7D5',
  borderInk: '#D9D2BF',
  errorBg: '#FEF2F2',
  errorText: '#B42318',
  errorBorder: '#FECACA',
  warnBg: '#FFF7ED',
  warnText: '#9A3412',
  warnBorder: '#FED7AA',
};

const HEADING_FONT = "'Nunito', sans-serif";
const BODY_FONT = "'Source Sans 3', sans-serif";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(seconds) {
  if (seconds < 0) seconds = 0;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function clientId() {
  return 'c_' + Math.random().toString(36).slice(2, 11);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// Markdown renderer used for Lantern's responses. Styled to read well on the
// dark navy Lantern bubble: white text on dark background. Block elements
// share a 10px bottom margin for consistent vertical rhythm; the trailing
// margin is reset via a CSS rule on .lantern-md > *:last-child so bubble
// padding stays balanced. Children's typed messages are NOT rendered as
// markdown — they're shown verbatim in the user-bubble branch above.
const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const BLOCK_BOTTOM = '0 0 10px';

const MD_COMPONENTS = {
  p: ({ node, ...props }) => (
    <p style={{ margin: BLOCK_BOTTOM }} {...props} />
  ),
  ul: ({ node, ordered, ...props }) => (
    <ul style={{ margin: BLOCK_BOTTOM, paddingLeft: 22 }} {...props} />
  ),
  ol: ({ node, ordered, ...props }) => (
    <ol style={{ margin: BLOCK_BOTTOM, paddingLeft: 22 }} {...props} />
  ),
  li: ({ node, ordered, checked, ...props }) => (
    <li style={{ margin: '2px 0' }} {...props} />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      style={{
        margin: BLOCK_BOTTOM,
        padding: '4px 12px',
        // Amber accent matches the brand's child-interface accent colour.
        borderLeft: `3px solid ${C.accent}`,
        opacity: 0.92,
      }}
      {...props}
    />
  ),
  // Inline code only. Block code (fenced) is wrapped by react-markdown in a
  // <pre>, which we override below to add the language badge and block styling.
  // For fenced blocks WITHOUT a language we still pass the className through
  // so the `pre` override can detect "no language" and skip the badge.
  code: ({ node, className, children, ...props }) => {
    const isFencedBlock = /language-/.test(className || '')
      || (typeof children === 'string' && children.includes('\n'))
      || (Array.isArray(children) && children.some(c => typeof c === 'string' && c.includes('\n')));
    if (isFencedBlock) {
      // Block — let `pre` handle styling. Pass className through so the badge
      // can read the language.
      return <code className={className} {...props}>{children}</code>;
    }
    return (
      <code
        style={{
          background: 'rgba(255,255,255,0.12)',
          padding: '1px 6px',
          borderRadius: 4,
          fontFamily: MONO_FONT,
          fontSize: '0.92em',
        }}
        {...props}
      >
        {children}
      </code>
    );
  },
  // Block code wrapper. Adds a small language label (top-right) when the
  // fence specifies a language (e.g. ```python). Body styling: dark bg,
  // rounded, monospace, horizontal scroll for long lines.
  pre: ({ node, children, ...props }) => {
    const child = React.Children.toArray(children)[0];
    const codeClass = child && child.props ? (child.props.className || '') : '';
    const langMatch = /language-(\w+)/.exec(codeClass);
    const language = langMatch ? langMatch[1] : null;
    return (
      <div style={{ position: 'relative', margin: BLOCK_BOTTOM }}>
        {language && (
          <span
            style={{
              position: 'absolute',
              top: 6,
              right: 10,
              fontSize: 10.5,
              color: 'rgba(255,255,255,0.55)',
              fontFamily: MONO_FONT,
              letterSpacing: '0.6px',
              textTransform: 'lowercase',
              pointerEvents: 'none',
              userSelect: 'none',
            }}
          >
            {language}
          </span>
        )}
        <pre
          style={{
            margin: 0,
            background: 'rgba(0,0,0,0.32)',
            // Extra right padding leaves room for the language badge.
            padding: language ? '12px 16px 12px 14px' : '12px 14px',
            borderRadius: 10,
            fontFamily: MONO_FONT,
            fontSize: '0.92em',
            overflowX: 'auto',
            whiteSpace: 'pre',
            lineHeight: 1.5,
          }}
          {...props}
        >
          {children}
        </pre>
      </div>
    );
  },
  table: ({ node, ...props }) => (
    <div style={{ overflowX: 'auto', margin: BLOCK_BOTTOM }}>
      <table
        style={{
          borderCollapse: 'collapse',
          width: '100%',
          fontSize: '0.95em',
        }}
        {...props}
      />
    </div>
  ),
  th: ({ node, ...props }) => (
    <th
      style={{
        border: '1px solid rgba(255,255,255,0.25)',
        padding: '7px 10px',
        textAlign: 'left',
        background: 'rgba(255,255,255,0.08)',
        fontWeight: 600,
      }}
      {...props}
    />
  ),
  td: ({ node, ...props }) => (
    <td
      style={{
        border: '1px solid rgba(255,255,255,0.18)',
        padding: '7px 10px',
      }}
      {...props}
    />
  ),
};

// Wrapper around ReactMarkdown so the children-of-the-bubble JSX stays tidy.
// The scoped <style> handles three things that are awkward to do inline:
//   - zero the trailing margin from the LAST block child so bubble padding
//     stays balanced top-to-bottom
//   - alternating-row backgrounds on tables (zebra striping)
//   - hover highlight on table rows for a slightly more premium feel
function LanternMarkdown({ children }) {
  return (
    <>
      <style>{`
        .lantern-md > *:last-child { margin-bottom: 0 !important; }
        .lantern-md tbody tr:nth-child(even) td { background: rgba(255,255,255,0.04); }
        .lantern-md tbody tr:hover td { background: rgba(255,255,255,0.08); }
      `}</style>
      <div className="lantern-md">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
          {children}
        </ReactMarkdown>
      </div>
    </>
  );
}

function MessageBubble({ message, sessionId, readOnly = false }) {
  const isChild = message.role === 'child';
  const isSystem = message.blocked || message.flagged;
  // Prior-session messages render with reduced opacity to visually
  // distinguish them from the live conversation, and they suppress the
  // ReportButton so the child can't flag a message from a session that
  // has already been sealed. TTS is also not triggered on these — that
  // happens in handleSend, which prior bubbles never go through.
  const wrapperStyle = readOnly ? { opacity: 0.7 } : undefined;

  if (isChild) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14, ...wrapperStyle }}>
        <div style={{
          maxWidth: '78%',
          background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDark} 100%)`,
          color: '#fff',
          borderRadius: '22px 22px 6px 22px',
          padding: '12px 16px',
          fontSize: 16,
          lineHeight: 1.5,
          fontFamily: BODY_FONT,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          boxShadow: `0 2px 8px ${C.accent}40, 0 1px 2px ${C.accentDark}30`,
          fontWeight: 500,
        }}>
          {message.content}
        </div>
      </div>
    );
  }

  // Lantern message — different style for system (blocked/flagged) vs normal
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 14, gap: 6, ...wrapperStyle }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 10,
        maxWidth: '88%',
      }}>
        <div style={{ flexShrink: 0, marginBottom: 2 }}>
          <LanternMark size={28} glow={false} />
        </div>
        <div style={{
          background: isSystem ? C.accentSoft : C.text,
          color: isSystem ? C.text : '#FFFEFB',
          border: isSystem ? `1.5px solid ${C.accent}` : 'none',
          borderRadius: '22px 22px 22px 6px',
          padding: '12px 16px',
          fontSize: 16,
          // Looser line-height + tiny positive letter-spacing read better in
          // longer assistant responses; system bubbles are usually short so
          // the same values are fine there too.
          lineHeight: 1.65,
          letterSpacing: '0.5px',
          fontFamily: BODY_FONT,
          // No whiteSpace:pre-wrap — react-markdown renders its own block
          // structure (paragraphs, lists, code blocks). Pre-wrap would
          // duplicate spacing inside <p> blocks and break tables.
          wordBreak: 'break-word',
          boxShadow: isSystem ? 'none' : '0 2px 8px rgba(26, 31, 54, 0.18), 0 1px 2px rgba(26, 31, 54, 0.08)',
        }}>
          {isSystem && (
            <div style={{
              fontFamily: HEADING_FONT,
              fontSize: 13,
              fontWeight: 800,
              color: C.accentDark,
              marginBottom: 6,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              letterSpacing: '0.2px',
            }}>
              <span aria-hidden="true" style={{ fontSize: 14 }}>🛡️</span>
              A note from Lantern
            </div>
          )}
          <LanternMarkdown>{message.content}</LanternMarkdown>
        </div>
      </div>
      {/* ReportButton — only on real Lantern messages (have a server
          messageId) AND only on live messages. Prior-session bubbles are
          read-only; reporting a message from a sealed session would be
          confusing and the child report flow doesn't support it. */}
      {!readOnly && message.serverMessageId && (
        <div style={{ marginLeft: 38 }}>
          <ReportButton sessionId={sessionId} messageId={message.serverMessageId} />
        </div>
      )}
    </div>
  );
}

// Shows the dots, and — once the wait is long enough to need explaining —
// a line of honest reassurance underneath them.
//
// Adaptive thinking made Lantern slower to first word, and a long silence
// reads as "broken" to a child. The messages claim only what we can actually
// observe (a request is in flight, this long has passed); they never pretend
// to know which internal phase Lantern is in.
function TypingIndicator({ reduceMotion }) {
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    // 500ms is frequent enough that a stage change feels responsive and slow
    // enough to be invisible on battery.
    const id = setInterval(() => setElapsedMs(Date.now() - startedAt), 500);
    return () => clearInterval(id);
  }, []);

  const message = thinkingMessage(elapsedMs);

  return (
    <div style={{ marginBottom: 14 }}>
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10 }}>
      <div style={{ flexShrink: 0, marginBottom: 2 }}>
        <LanternMark size={28} glow={false} />
      </div>
      <div style={{
        background: C.text,
        borderRadius: '22px 22px 22px 6px',
        padding: '14px 18px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        boxShadow: '0 2px 8px rgba(26, 31, 54, 0.18)',
      }}>
        {[0, 1, 2].map(i => (
          <span
            key={i}
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: '#fff',
              opacity: 0.85,
              // Suppressed when the parent has asked for reduced motion, or
              // the device has. The dots stay visible, they just stop moving.
              animation: reduceMotion
                ? 'none'
                : `chat-dot 1.1s infinite ease-in-out ${i * 0.15}s`,
            }}
          />
        ))}
      </div>
    </div>

      {/* aria-live so a child using a screen reader is told Lantern is still
          working, rather than sitting in silence wondering. 'polite' so it
          never interrupts them mid-sentence. */}
      {message && (
        <p
          aria-live="polite"
          style={{
            margin: '8px 0 0 38px',
            fontSize: 13,
            fontStyle: 'italic',
            color: C.textMuted,
          }}
        >
          {message}
        </p>
      )}
    </div>
  );
}

// ─── Main chat ────────────────────────────────────────────────────────────────

// Turns a failed chat load into something a child can act on.
//
// Every failure used to render the same sentence — "Could not start the
// chat. Please try again." — no matter what actually went wrong, so a
// child locked out by their active-hours setting, a child whose session had
// vanished, and a child who simply lost signal all saw the identical
// message, and so did we when they reported it.
//
// The 403s raised by routes/child.js are all written to be read by a child
// (the active-hours message names the hours their parent set), so those are
// surfaced verbatim. Everything else maps to copy authored here — server
// internals never reach a child.
function describeChatLoadError(err) {
  // No response at all: offline, flaky signal, or the server restarting
  // mid-request (a deploy does exactly this). Worth distinguishing, because
  // "try again" genuinely does work for this one.
  if (!err || !err.response) {
    return "We couldn't reach Lantern just then. Check your internet and try again.";
  }

  const { status, data } = err.response;
  const serverMessage = typeof data?.error === 'string' ? data.error : null;

  if (status === 403 && serverMessage) return serverMessage;
  if (status === 403) return "This chat isn't available right now.";
  if (status === 404) return "We couldn't find that chat. It may have been removed.";
  if (status === 429) return 'Lantern is a bit busy. Wait a moment and try again.';
  if (status >= 500)  return 'Something went wrong at our end. Please try again.';

  return 'Could not start the chat. Please try again.';
}

export default function Chat() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [config, setConfig]               = useState(null);

  // Reading accessibility (migration 019). Declared after `config` because it
  // reads it. Falls back to today's appearance until config arrives, so
  // nothing flashes unstyled on a slow connection.
  const a11y = accessibilityStyles(config && config.accessibility, BODY_FONT);
  const reduceMotion = shouldReduceMotion(config && config.accessibility);
  const [configError, setConfigError]     = useState(null);
  // Bumped by the "Try again" button to re-run the load effect.
  const [reloadNonce, setReloadNonce]     = useState(0);
  const [messages, setMessages]           = useState([]);
  const [input, setInput]                 = useState('');
  const [sending, setSending]             = useState(false);
  const [sendError, setSendError]         = useState(null);
  const [rateLimitNotice, setRateLimitNotice] = useState(null);
  const [secondsLeft, setSecondsLeft]     = useState(null);
  // Grace-period state. Once secondsLeft hits zero we don't seal immediately
  // — the child gets a 5-minute "finish your thought" window with an amber
  // warning banner. State is driven by computeSessionTimerPhase against the
  // server-truth startedAt, so refreshing mid-grace recovers the right
  // phase rather than resetting the timer.
  const [startedAtMs, setStartedAtMs]     = useState(null);
  const [graceSecondsLeft, setGraceSecondsLeft] = useState(null);
  const [inGracePeriod, setInGracePeriod] = useState(false);
  const [bannerDismissed, setBannerDismissed]   = useState(false);
  const [listening, setListening]         = useState(false);
  const [voiceError, setVoiceError]       = useState(null);
  const [endingSession, setEndingSession] = useState(false);
  // Mode of THIS session — surfaced by GET /session/:id/messages alongside
  // startedAt. Used to gate the 11+ auto-opener and to suppress the generic
  // welcome chips (which talk about "homework" and don't fit 11+ prep).
  const [sessionMode, setSessionMode]     = useState(null);
  // Prior-session messages surfaced by the same endpoint — the last 3–4
  // turns from a recently-sealed session in the same subject + mode.
  // Rendered above the live transcript under a "Continuing from your last
  // session" divider, in read-only mode (no ReportButton, no TTS, dimmed
  // opacity) so the child can see what Lantern is referring to when it
  // opens with "shall we pick up from there?".
  const [priorMessages, setPriorMessages] = useState([]);
  // Per-session mute, only relevant when parent has enabled voice output.
  // Doesn't persist — next session starts unmuted again.
  const [voiceOutputMuted, setVoiceOutputMuted] = useState(false);

  // PWA-mode back-button visibility. iOS standalone (Add-to-Home-Screen)
  // hides the Safari chrome entirely, leaving children with no way to
  // leave a session except the timer or the End button. We surface our
  // own back button in those cases. Also show when there's no browser
  // history to fall back on (deep-link or single-page restore) so a
  // direct landing on /child/chat/:id isn't a dead end. Computed once
  // on mount — these conditions don't change during the session.
  const [showPwaBack] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const standaloneByQuery = window.matchMedia?.('(display-mode: standalone)').matches;
      const standaloneByNavApi = window.navigator?.standalone === true;
      const noHistory = window.history?.length <= 1;
      return !!(standaloneByQuery || standaloneByNavApi || noHistory);
    } catch {
      return false;
    }
  });

  const messagesEndRef    = useRef(null);
  const messagesScrollRef = useRef(null);
  const voiceInputRef     = useRef(null);
  const voiceOutputRef    = useRef(null);
  const inputRef          = useRef(null);
  // Refs that always hold the latest handleSend / handleEndSession. The voice
  // and timer effects below depend on stable values (config / secondsLeft)
  // only, but still need to call the up-to-date handler — so we read them
  // through these refs at call time.
  const handleSendRef       = useRef(null);
  const handleEndSessionRef = useRef(null);
  // Guard so the 11+ auto-opener fires exactly once per component instance —
  // protects against React strict-mode double-invocation in dev and against
  // accidental re-fires if the load effect ever re-runs. The server also
  // enforces idempotency (409 on a second SESSION_START), but blocking on the
  // client avoids a wasted round-trip.
  const openerFiredRef      = useRef(false);

  // Background-time tracking for the session timer. The PWA can be in the
  // background for minutes-to-hours when a child puts the device down or
  // switches apps — that time should NOT count against their session
  // allowance. We stamp the moment the tab/window hides and accumulate the
  // chunk into totalBackgroundMs when it returns. The timer effect reads
  // totalBackgroundMs.current on every tick and passes it to
  // computeSessionTimerPhase, which subtracts it from elapsed.
  const backgroundedAtRef   = useRef(null);
  const totalBackgroundMsRef = useRef(0);
  // Ref to the live tick function so the visibilitychange handler (defined
  // in its own effect) can trigger an immediate recompute on foreground
  // return rather than waiting up to a second for the next setInterval.
  const tickRef             = useRef(null);

  // Load session config + seed any prior messages for this session so a
  // refresh/back-navigation doesn't lose the conversation in flight.
  useEffect(() => {
    let cancelled = false;
    // getSessionMessages failures are tolerated — the chat can still load with
    // an empty fallback — but they must NOT be silenced. Log full error
    // context so a production incident leaves a paper trail in the browser
    // console (and any error tracker hooked into it), and surface a
    // user-visible cue via setSendError below if the fallback actually fires.
    let messagesLoadFailed = false;
    Promise.all([
      getSessionConfig(),
      getSessionMessages(sessionId).catch((err) => {
        messagesLoadFailed = true;
        console.error('[Chat] getSessionMessages failed — falling back to empty state:', err);
        return { data: { messages: [], priorMessages: [], session: null, _loadError: true } };
      }),
    ])
      .then(([cfgRes, msgRes]) => {
        if (cancelled) return;
        setConfig(cfgRes.data);

        // Compute remaining time from the actual session start. Without this,
        // resuming a session would reset the timer to the full limit. If the
        // server didn't return startedAt (e.g. fetch failed and the catch
        // above returned the empty fallback), fall back to "now" so the
        // timer ticks from the full limit.
        const startedAt = msgRes.data?.session?.startedAt || Date.now();
        setStartedAtMs(startedAt);

        // Initialise the displayed timer + grace phase from the helper so a
        // refresh mid-grace recovers the right state rather than resetting.
        // totalBackgroundMs is 0 at first load so the initial computation
        // matches the pre-background-tracking behaviour; we still pass it
        // for symmetry with the tick loop below.
        const initial = computeSessionTimerPhase({
          startedAtMs:      startedAt,
          timeLimitMinutes: cfgRes.data.timeLimit,
          nowMs:            Date.now(),
          backgroundMs:     totalBackgroundMsRef.current,
        });
        setSecondsLeft(initial.secondsLeft);
        if (initial.phase === 'grace') {
          setInGracePeriod(true);
          setGraceSecondsLeft(initial.graceSecondsLeft);
        } else if (initial.phase === 'expired') {
          // Past the grace window — seal immediately. The timer effect
          // below will also catch this on next tick; calling handleEnd
          // here lets the UI bail out fast.
          setInGracePeriod(true);
          setGraceSecondsLeft(0);
        }

        const mode = msgRes.data?.session?.mode || null;
        setSessionMode(mode);

        // If getSessionMessages threw and the empty fallback was used,
        // surface a soft visible cue so the child knows something
        // went wrong rather than wondering why their conversation
        // is missing. The chat is still usable — they can type and
        // continue. Critical: this is a visible message, NOT a
        // navigate() call. We do NOT silently redirect on error.
        if (messagesLoadFailed) {
          setSendError(
            "We couldn't load your previous messages just now. " +
            "You can keep typing — your new messages will be saved normally."
          );
        }

        // Prior-session bubbles for the continuity divider. The server
        // already filters to "fresh sessions only" — once this session
        // has its own turns, priorMessages comes back empty so the
        // divider disappears on resume.
        //
        // Defensive projection: a malformed individual prior-message
        // entry (e.g. missing id, content of unexpected type) must NOT
        // throw out of the .map() and kill the whole load effect. Each
        // row is wrapped — bad rows are skipped with a console log so
        // the failure is debuggable without breaking the child's session.
        const priorRaw = Array.isArray(msgRes.data?.priorMessages)
          ? msgRes.data.priorMessages
          : [];
        const prior4 = [];
        for (const m of priorRaw) {
          try {
            if (!m || typeof m !== 'object') continue;
            prior4.push({
              id:      `prior:${m.id}`,
              role:    m.role === 'assistant' ? 'lantern' : m.role,
              content: m.content,
            });
          } catch (rowErr) {
            console.error('[Chat] prior message projection failed for row, skipping:', m, rowErr);
          }
        }
        setPriorMessages(prior4);

        // Live messages — same defensive projection. The server flags
        // per-row decrypt failures with `decryptFailed: true` and a
        // placeholder content string; we render those as-is so the
        // child can see we know about the gap.
        const liveRaw = Array.isArray(msgRes.data?.messages)
          ? msgRes.data.messages
          : [];
        const prior = [];
        for (const m of liveRaw) {
          try {
            if (!m || typeof m !== 'object') continue;
            prior.push({
              id: m.id,
              role: m.role === 'assistant' ? 'lantern' : m.role,
              content: m.content,
              inputMethod: m.inputMethod,
              decryptFailed: m.decryptFailed === true,
              // Stored assistant messages are real — wire ReportButton against
              // their server id so the child can flag prior turns.
              serverMessageId: m.role === 'assistant' ? m.id : null,
            });
          } catch (rowErr) {
            console.error('[Chat] live message projection failed for row, skipping:', m, rowErr);
          }
        }
        setMessages(prior);

        // 11+ auto-opener — fire ONCE when a fresh eleven_plus session has
        // no prior messages, so Lantern proactively asks "Verbal Reasoning
        // or Mathematics?" before the child types anything. Resume cases
        // (prior.length > 0) skip this entirely; the server also rejects a
        // SESSION_START on any non-empty 11+ session with a 409. Voice
        // output is intentionally NOT triggered for the opener — iOS Safari
        // blocks Audio.play() until the first user gesture, and the audio-
        // unlock listener (set up in a separate effect) hasn't run yet.
        // Subsequent replies play normally once the child interacts.
        if (mode === 'eleven_plus' && prior.length === 0 && !openerFiredRef.current) {
          openerFiredRef.current = true;
          setSending(true);
          sendChatMessage(sessionId, 'SESSION_START', 'text')
            .then((res) => {
              if (cancelled) return;
              const data = res.data;
              setMessages(m => [...m, {
                id: clientId(),
                role: 'lantern',
                content: data.response,
                blocked: data.blocked === true,
                flagged: data.flagged === true,
                serverMessageId: (data.blocked || data.flagged) ? null : data.messageId,
              }]);
            })
            .catch((err) => {
              if (cancelled) return;
              console.error('[Chat] 11+ opener failed:', err);
              // Soft failure — the child can still type to start the
              // session. The model's system prompt still mandates the
              // VR-or-Maths opening, so any first message will elicit it.
              setSendError("Lantern couldn't quite get started — type hello to begin.");
            })
            .finally(() => {
              if (cancelled) return;
              setSending(false);
            });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // Log every chat-load failure so production incidents leave a trail.
        // The two navigate-able outcomes are: (a) 401 → /login (explicit
        // auth flow, expected), and (b) configError screen (no navigate
        // at all). We do NOT silently redirect to the home / subjects
        // screen on any error — that pattern was the source of the
        // 17 May silent-redirect incident.
        console.error('[Chat] load effect failed:', err);
        if (err.response?.status === 401) {
          navigate('/login');
          return;
        }
        setConfigError(describeChatLoadError(err));
      });
    return () => { cancelled = true; };
  }, [navigate, sessionId, reloadNonce]);

  // Initialise voice services once we know the config. The transcript callback
  // routes through handleSendRef so we always call the latest handleSend
  // without re-creating VoiceInput on every render (which would interrupt
  // active listening).
  // iOS Safari blocks Audio.play() unless it originates from a user gesture.
  // Listen once for the first tap or click anywhere on the page and unlock
  // the audio session — every speak() after that point plays normally.
  // Listener is one-shot; both events use { once: true } so only the very
  // first interaction does the unlock work.
  useEffect(() => {
    function handler() { unlockAudio(); }
    document.addEventListener('touchstart', handler, { once: true, passive: true });
    document.addEventListener('click',      handler, { once: true });
    return () => {
      document.removeEventListener('touchstart', handler);
      document.removeEventListener('click',      handler);
    };
  }, []);

  useEffect(() => {
    if (!config) return;
    // VoiceOutputTTS calls the server's Cloud TTS endpoint and falls back to
    // Web Speech per-utterance if the API call fails. Same speak/stop shape.
    voiceOutputRef.current = new VoiceOutputTTS();
    voiceInputRef.current = new VoiceInput(
      (transcript) => {
        setListening(false);
        if (transcript && transcript.trim()) {
          handleSendRef.current?.(transcript.trim(), 'voice');
        }
      },
      (err) => {
        setListening(false);
        setVoiceError(err.message || 'Could not hear you. Try again?');
      }
    );
    return () => {
      voiceInputRef.current?.stopListening();
      voiceOutputRef.current?.stop();
    };
  }, [config]);

  // Session timer — ticks once a second and uses computeSessionTimerPhase
  // to derive whether we're active, inside the 5-minute grace window, or
  // expired. The grace window is the difference between this implementation
  // and a naive "seal at zero" timer: the child loses no thought mid-
  // question because the session stays open while the amber banner counts
  // them down. When phase flips to 'expired' the session seals through
  // handleEndSessionRef so the existing end-session machinery (learning
  // context generation, navigation) all run unchanged.
  useEffect(() => {
    if (!config || startedAtMs === null) return;
    let cancelled = false;

    function tick() {
      if (cancelled) return;
      // Skip seal decisions while the tab is hidden. Browsers throttle
      // setInterval in the background but don't always stop it; if the
      // session is in the grace window when the child puts their device
      // down, a stray tick must NOT seal it under them. The visibility
      // handler triggers an immediate tick on return so any expired-on-
      // resume case is still caught — just with totalBackgroundMs up to
      // date so the helper makes the right call.
      if (typeof document !== 'undefined' && document.hidden) return;

      const result = computeSessionTimerPhase({
        startedAtMs,
        timeLimitMinutes: config.timeLimit,
        nowMs:            Date.now(),
        backgroundMs:     totalBackgroundMsRef.current,
      });

      setSecondsLeft(result.secondsLeft);

      if (result.phase === 'grace') {
        setInGracePeriod(true);
        setGraceSecondsLeft(result.graceSecondsLeft);
        // 1-minute reappear: the helper sets bannerForcedVisible once
        // grace drops under 60s — force the banner back even if the
        // child previously dismissed it.
        if (result.bannerForcedVisible) setBannerDismissed(false);
      } else if (result.phase === 'expired') {
        handleEndSessionRef.current?.();
        return;
      }
    }
    tickRef.current = tick;

    tick(); // run once immediately so a refresh in grace doesn't show stale state
    const id = setInterval(tick, 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
      tickRef.current = null;
    };
  }, [config, startedAtMs]);

  // Page Visibility API — pause the session timer while the PWA is in
  // the background so a child who puts the device down for 20 minutes
  // doesn't lose 20 minutes from their session allowance. On hide, stamp
  // backgroundedAt; on show, fold the chunk into totalBackgroundMs and
  // trigger an immediate tick so the displayed timer updates in the same
  // frame rather than after the next interval. Listener is registered
  // once and cleaned up on unmount.
  useEffect(() => {
    function onVisibilityChange() {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        backgroundedAtRef.current = Date.now();
      } else {
        if (backgroundedAtRef.current !== null) {
          totalBackgroundMsRef.current += Date.now() - backgroundedAtRef.current;
          backgroundedAtRef.current = null;
        }
        // Force an immediate recompute so the timer/phase update reflects
        // the new background offset without waiting up to a second.
        tickRef.current?.();
      }
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
    };
  }, []);

  // Auto-scroll inside the messages container (NOT the page) on new messages
  useEffect(() => {
    const el = messagesScrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, sending]);

  async function handleSend(text, inputMethod = 'text') {
    const content = (text ?? input).trim();
    if (!content || sending) return;

    setInput('');
    setSendError(null);
    setRateLimitNotice(null);
    setVoiceError(null);

    const childMessage = {
      id: clientId(),
      role: 'child',
      content,
      inputMethod,
    };
    setMessages(m => [...m, childMessage]);
    setSending(true);

    try {
      const res = await sendChatMessage(sessionId, content, inputMethod);
      const data = res.data;
      const lanternMessage = {
        id: clientId(),
        role: 'lantern',
        content: data.response,
        blocked: data.blocked === true,
        flagged: data.flagged === true,
        // Only real Lantern messages get a server messageId; blocked/flagged
        // responses are server-generated safety messages and not stored.
        serverMessageId: (data.blocked || data.flagged) ? null : data.messageId,
      };
      setMessages(m => [...m, lanternMessage]);

      if (config?.voiceOutputEnabled && !voiceOutputMuted && voiceOutputRef.current?.isSupported()) {
        voiceOutputRef.current.speak(data.response, { voiceName: config?.voiceName }).catch(() => {});
      }
    } catch (err) {
      // Log the full error to the browser console before the UX takes over
      // with a friendlier message. Axios errors print interactively in dev
      // tools so status, response body, headers, and config are all clickable
      // — invaluable when a parent reports "it just failed".
      console.error('[Chat] sendChatMessage failed:', err);
      const status = err?.response?.status;
      if (status === 401) {
        navigate('/login');
        return;
      }
      if (status === 429) {
        // Rate limit isn't an error — the child has been chatting a lot,
        // which is great. Render in amber rather than red so it reads as
        // a soft pause-and-resume cue rather than a fault state.
        setRateLimitNotice('You have been chatting a lot — well done! Please wait a few minutes and then carry on.');
      } else if (status === 409) {
        setSendError('This session has ended. Start a new one to keep going.');
      } else {
        setSendError("Couldn't send your message. Try again?");
      }
    } finally {
      setSending(false);
      // Refocus the input so the child can keep typing without tapping again
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }

  async function handleEndSession() {
    if (endingSession) return;
    setEndingSession(true);
    voiceInputRef.current?.stopListening();
    voiceOutputRef.current?.stop();
    try {
      await endSession(sessionId);
    } catch {
      // Even if the server end-call fails, navigate away — the session will
      // either time out server-side or be sealed when the next message lands.
    }
    navigate('/child/subjects');
  }

  // Keep the refs pointing at the latest handler so the voice and timer
  // callbacks above always invoke the current closure.
  useEffect(() => {
    handleSendRef.current = handleSend;
    handleEndSessionRef.current = handleEndSession;
  });

  function toggleListening() {
    if (!voiceInputRef.current) return;
    setVoiceError(null);
    if (listening) {
      voiceInputRef.current.stopListening();
      setListening(false);
    } else {
      if (!voiceInputRef.current.isSupported()) {
        setVoiceError('Voice typing only works in Chrome or Edge.');
        return;
      }
      voiceInputRef.current.startListening();
      setListening(true);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  // Timer state — derives both pill style AND dot colour
  const timerState = secondsLeft === null
    ? 'idle'
    : secondsLeft < 60 ? 'urgent'
    : secondsLeft < 300 ? 'warning'
    : 'normal';

  const timerStyles = {
    idle:    { bg: 'transparent', border: 'transparent',   fg: C.textMuted, dot: C.borderInk },
    normal:  { bg: 'transparent', border: 'transparent',   fg: C.textMuted, dot: C.green },
    warning: { bg: C.warnBg,      border: C.warnBorder,    fg: C.warnText,  dot: C.accentDark },
    urgent:  { bg: C.errorBg,     border: C.errorBorder,   fg: C.errorText, dot: C.errorText },
  }[timerState];

  if (configError) {
    return (
      <div style={{
        minHeight: '100vh',
        background: C.bg,
        fontFamily: BODY_FONT,
        color: C.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}>
        <div style={{ maxWidth: 420, width: '100%', textAlign: 'center' }}>
          <div role="alert" style={{
            padding: '24px 28px',
            background: C.errorBg,
            border: `1.5px solid ${C.errorBorder}`,
            borderRadius: 20,
            color: C.errorText,
            fontSize: 15,
            fontWeight: 600,
          }}>
            {configError}
          </div>

          {/* This screen used to be a dead end: the message and nothing
              else. A child who landed here had no way out of it at all
              short of force-quitting the app — which a younger child will
              not think to do. Both routes out are always offered. */}
          <div style={{
            display: 'flex',
            gap: 10,
            marginTop: 18,
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            <button
              type="button"
              onClick={() => { setConfigError(null); setReloadNonce((n) => n + 1); }}
              style={{
                flex: '1 1 150px',
                minHeight: 52,
                padding: '14px 22px',
                borderRadius: 16,
                border: 'none',
                background: C.accent,
                color: '#fff',
                fontFamily: HEADING_FONT,
                fontWeight: 800,
                fontSize: 16,
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => navigate('/child')}
              style={{
                flex: '1 1 150px',
                minHeight: 52,
                padding: '14px 22px',
                borderRadius: 16,
                border: `1.5px solid ${C.errorBorder}`,
                background: 'transparent',
                color: C.text,
                fontFamily: HEADING_FONT,
                fontWeight: 800,
                fontSize: 16,
                cursor: 'pointer',
              }}
            >
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes chat-dot {
          0%, 60%, 100% { transform: scale(0.6); opacity: 0.4; }
          30%           { transform: scale(1);   opacity: 1; }
        }
        @keyframes chat-pulse-ring {
          0%   { box-shadow: 0 0 0 0    rgba(239, 68, 68, 0.55); }
          70%  { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);    }
          100% { box-shadow: 0 0 0 0    rgba(239, 68, 68, 0);    }
        }
        @keyframes chat-urgent-pulse {
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.04); }
        }
        @keyframes chat-msg-in {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes chat-loading-glow {
          0%, 100% { opacity: 0.85; transform: scale(1); }
          50%      { opacity: 1;    transform: scale(1.06); }
        }
        .chat-input {
          flex: 1;
          resize: none;
          min-height: 48px;
          max-height: 140px;
          padding: 13px 16px;
          font-family: ${BODY_FONT};
          font-size: 16px;
          line-height: 1.4;
          color: ${C.text};
          background: ${C.bg};
          border: 2px solid ${C.border};
          border-radius: 18px;
          outline: none;
          transition: border-color 160ms ease, box-shadow 160ms ease, background 160ms ease;
          font-weight: 500;
          box-sizing: border-box;
        }
        .chat-input:focus {
          border-color: ${C.accent};
          background: ${C.card};
          box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.18);
        }
        .chat-input::placeholder { color: #B0B5C0; }
        .chat-input:disabled { opacity: 0.6; cursor: not-allowed; }

        .chat-icon-btn {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          border: 2px solid ${C.border};
          background: ${C.card};
          color: ${C.accentDark};
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 160ms ease, border-color 160ms ease, transform 90ms ease;
          flex-shrink: 0;
          -webkit-tap-highlight-color: transparent;
        }
        .chat-icon-btn:hover:not(:disabled) {
          background: ${C.accentSoft};
          border-color: ${C.accent};
        }
        .chat-icon-btn:active:not(:disabled) { transform: scale(0.95); }
        .chat-icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .chat-mic--listening {
          background: #FEE2E2 !important;
          border-color: #EF4444 !important;
          color: #B91C1C !important;
          animation: ${reduceMotion ? 'none' : 'chat-pulse-ring 1.4s ease-out infinite'};
        }

        .chat-send {
          height: 48px;
          width: 48px;
          padding: 0;
          border-radius: 50%;
          border: none;
          background: ${C.accent};
          color: #fff;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 3px 0 ${C.accentDark}, 0 6px 14px ${C.accent}55;
          transition: background 160ms ease, transform 90ms ease, box-shadow 160ms ease;
          flex-shrink: 0;
        }
        .chat-send:hover:not(:disabled) { background: ${C.accentDark}; }
        .chat-send:active:not(:disabled) {
          transform: translateY(2px);
          box-shadow: 0 1px 0 ${C.accentDark}, 0 2px 6px ${C.accent}40;
        }
        .chat-send:disabled {
          opacity: 0.45;
          cursor: not-allowed;
          box-shadow: 0 3px 0 ${C.accentDark};
        }

        .chat-mute-btn {
          width: 36px;
          height: 36px;
          padding: 0;
          border-radius: 12px;
          border: 1.5px solid ${C.border};
          background: ${C.card};
          color: ${C.text};
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background 160ms ease, border-color 160ms ease, color 160ms ease;
          flex-shrink: 0;
          -webkit-tap-highlight-color: transparent;
        }
        .chat-mute-btn:hover {
          background: ${C.accentSoft};
          border-color: ${C.accent};
          color: ${C.accentDark};
        }
        .chat-mute-btn[aria-pressed="true"] {
          background: ${C.accentSoft};
          border-color: ${C.accent};
          color: ${C.accentDark};
        }

        .chat-end-btn {
          padding: 7px 12px;
          border-radius: 12px;
          border: 1.5px solid ${C.border};
          background: ${C.card};
          color: ${C.text};
          font-family: ${HEADING_FONT};
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
          transition: background 160ms ease, border-color 160ms ease, color 160ms ease;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          -webkit-tap-highlight-color: transparent;
        }
        .chat-end-btn:hover:not(:disabled) {
          background: ${C.accentSoft};
          border-color: ${C.accent};
          color: ${C.accentDark};
        }
        .chat-end-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .chat-msg-anim { animation: chat-msg-in 220ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }

        .chat-scroll::-webkit-scrollbar { width: 0; }

        .chat-suggest {
          background: ${C.card};
          border: 1.5px solid ${C.border};
          border-radius: 999px;
          padding: 8px 14px;
          font-family: ${BODY_FONT};
          font-size: 14px;
          font-weight: 600;
          color: ${C.text};
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 7px;
          transition: background 160ms ease, border-color 160ms ease;
          box-shadow: 0 1px 2px rgba(26,31,54,0.04);
        }
        .chat-suggest:hover:not(:disabled) {
          background: ${C.accentSoft};
          border-color: ${C.accent};
        }
        .chat-suggest:disabled { opacity: 0.5; cursor: not-allowed; }

        .chat-back-btn {
          width: 32px;
          height: 32px;
          padding: 0;
          border-radius: 50%;
          border: 1.5px solid ${C.border};
          background: ${C.card};
          color: ${C.text};
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background 160ms ease, border-color 160ms ease, color 160ms ease, transform 90ms ease;
          flex-shrink: 0;
          -webkit-tap-highlight-color: transparent;
        }
        .chat-back-btn:hover:not(:disabled) {
          background: ${C.accentSoft};
          border-color: ${C.accent};
          color: ${C.accentDark};
        }
        .chat-back-btn:active:not(:disabled) { transform: scale(0.94); }
        .chat-back-btn:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
      <div style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: C.bg,
        color: C.text,
        position: 'relative',
        // Spread last so the parent's accessibility choices win over the
        // default typography. fontFamily/fontSize/lineHeight cascade to
        // every message bubble below rather than needing per-element rules.
        ...a11y,
      }}>
        {/* Decorative amber accent bar */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${C.accent} 0%, #FBBF24 50%, ${C.accent} 100%)`,
            zIndex: 2,
          }}
        />

        {/* Header */}
        <header style={{
          flexShrink: 0,
          background: C.card,
          borderBottom: `1px solid ${C.border}`,
          // env(safe-area-inset-top) is 0 in non-notched / non-PWA contexts
          // and ~44-50px under the iOS status bar in standalone PWA mode.
          // viewport-fit=cover on the meta tag (set in client/public/index.html)
          // is what makes the env() variable resolve to a real value.
          padding: 'calc(14px + env(safe-area-inset-top)) 16px 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
        }}>
          {showPwaBack && (
            <button
              type="button"
              className="chat-back-btn"
              onClick={() => navigate('/child/subjects')}
              aria-label="Back to subjects"
              title="Back"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path d="M10 3 L5 8 L10 13" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <LanternMark size={30} glow={false} />
            <span style={{
              fontFamily: HEADING_FONT,
              fontSize: 18,
              fontWeight: 800,
              color: C.text,
              letterSpacing: '-0.01em',
            }}>
              Lantern
            </span>
          </div>

          {/* Timer pill */}
          <div
            aria-live="polite"
            aria-label={`Time remaining: ${secondsLeft === null ? 'loading' : formatTime(secondsLeft)}`}
            style={{
              fontFamily: HEADING_FONT,
              fontSize: 13,
              fontWeight: 800,
              color: timerStyles.fg,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: timerState === 'warning' || timerState === 'urgent' ? '5px 11px' : '5px 8px',
              background: timerStyles.bg,
              border: `1.5px solid ${timerStyles.border}`,
              borderRadius: 999,
              fontVariantNumeric: 'tabular-nums',
              transition: 'background 200ms ease, border-color 200ms ease, color 200ms ease',
              animation: timerState === 'urgent' ? 'chat-urgent-pulse 1s ease-in-out infinite' : undefined,
            }}
          >
            <span aria-hidden="true" style={{
              width: 7, height: 7, borderRadius: '50%',
              background: timerStyles.dot,
              boxShadow: timerState === 'urgent' ? `0 0 0 3px ${C.errorBg}` : 'none',
            }} />
            {secondsLeft === null ? '—:—' : formatTime(secondsLeft)}
          </div>

          {/* Mute/unmute — only when parent has enabled voice output */}
          {config?.voiceOutputEnabled && (
            <button
              type="button"
              className="chat-mute-btn"
              onClick={() => {
                const next = !voiceOutputMuted;
                setVoiceOutputMuted(next);
                if (next) voiceOutputRef.current?.stop();
              }}
              aria-pressed={voiceOutputMuted}
              aria-label={voiceOutputMuted ? 'Unmute Lantern\'s voice' : 'Mute Lantern\'s voice'}
              title={voiceOutputMuted ? 'Unmute' : 'Mute'}
            >
              {voiceOutputMuted ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M11 5L6 9H3v6h3l5 4V5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                  <path d="M16 9l5 6M21 9l-5 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M11 5L6 9H3v6h3l5 4V5z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                  <path d="M16 8.5a4.5 4.5 0 0 1 0 7M19 5a8 8 0 0 1 0 14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                </svg>
              )}
            </button>
          )}

          <button
            type="button"
            className="chat-end-btn"
            onClick={handleEndSession}
            disabled={endingSession}
          >
            {endingSession ? 'Ending…' : (
              <>
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <rect x="2" y="2" width="8" height="8" rx="1.5" fill="currentColor" />
                </svg>
                End
              </>
            )}
          </button>
        </header>

        {/* Messages area */}
        <main
          ref={messagesScrollRef}
          className="chat-scroll"
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px 16px 14px',
            background: `${C.bg} radial-gradient(ellipse 500px 320px at 50% -20%, ${C.accent}14 0%, ${C.accent}00 65%)`,
          }}
        >
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            {/* Loading state */}
            {!config && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 16,
                padding: '80px 20px',
                color: C.textMuted,
              }}>
                <div style={{ animation: 'chat-loading-glow 1.6s ease-in-out infinite' }}>
                  <LanternMark size={56} glow={true} />
                </div>
                <p style={{
                  margin: 0,
                  fontFamily: HEADING_FONT,
                  fontWeight: 800,
                  fontSize: 16,
                  color: C.text,
                  letterSpacing: '-0.01em',
                }}>
                  Lighting things up…
                </p>
              </div>
            )}

            {/* Grace-period banner — fires once the main timer reaches 0
                and stays visible (unless dismissed) until grace expires.
                The 1-minute reappear logic in the timer effect resets
                bannerDismissed whenever grace drops under 60s so a child
                who dismissed the banner gets a final heads-up. */}
            {inGracePeriod && !bannerDismissed && graceSecondsLeft !== null && graceSecondsLeft > 0 && (
              <div
                role="alert"
                aria-live="polite"
                style={{
                  margin: '16px 0 20px',
                  padding: '14px 16px',
                  background: C.warnBg,
                  border: `1px solid ${C.warnBorder}`,
                  borderLeft: `4px solid ${C.accent}`,
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  color: C.warnText,
                }}
              >
                <span aria-hidden="true" style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: '#fff',
                  border: `1px solid ${C.warnBorder}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="6.5" stroke={C.accent} strokeWidth="1.6"/>
                    <path d="M8 4.5v4M8 10.5v.5" stroke={C.accent} strokeWidth="1.6" strokeLinecap="round"/>
                  </svg>
                </span>
                <div style={{ flex: 1, minWidth: 0, fontFamily: BODY_FONT, fontSize: 14, lineHeight: 1.45 }}>
                  <div style={{ fontWeight: 700 }}>
                    Your session time is up — you have {formatTime(graceSecondsLeft)} to finish your thought before Lantern saves your session.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setBannerDismissed(true)}
                  aria-label="Dismiss grace warning"
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: C.warnText,
                    fontSize: 20,
                    fontWeight: 700,
                    cursor: 'pointer',
                    padding: 4,
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >×</button>
              </div>
            )}

            {/* Welcome state — suppressed in 11+ mode where the auto-opener
                generates the first message itself, AND suppressed when the
                continuity divider is on screen (the prior bubbles are
                already a richer "what would you like to work on?" cue than
                the generic chips, which would otherwise stack below them). */}
            {config && sessionMode !== 'eleven_plus' && priorMessages.length === 0 && messages.length === 0 && !sending && (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                padding: '40px 20px 28px',
                color: C.text,
              }}>
                <div style={{ marginBottom: 12 }}>
                  <LanternMark size={84} />
                </div>
                <h1 style={{
                  margin: '14px 0 8px',
                  fontFamily: HEADING_FONT,
                  fontSize: 26,
                  fontWeight: 800,
                  letterSpacing: '-0.5px',
                  color: C.text,
                }}>
                  Hi {config.displayName}! ✨
                </h1>
                <p style={{
                  margin: 0,
                  fontSize: 16,
                  color: C.textMuted,
                  maxWidth: 320,
                  lineHeight: 1.5,
                }}>
                  What would you like to work on? Ask me anything — I'll help you think it through.
                </p>

                {/* Suggestion chips — clicking sends the chip text as the first message */}
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 8,
                  marginTop: 22,
                  maxWidth: 340,
                }}>
                  {[
                    { icon: '📚', label: 'Help with my homework' },
                    { icon: '💡', label: 'Explain something' },
                    { icon: '🧠', label: 'Quiz me' },
                  ].map(s => (
                    <button
                      key={s.label}
                      type="button"
                      className="chat-suggest"
                      onClick={() => handleSend(s.label)}
                      disabled={sending || endingSession}
                    >
                      <span aria-hidden="true">{s.icon}</span>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Prior-session messages — same bubble styling as live turns,
                but read-only (no ReportButton, no TTS) and dimmed to 0.7
                opacity so the child can see what Lantern is referring to
                without confusing them for current-conversation content.
                The divider below names the boundary. */}
            {priorMessages.length > 0 && (
              <>
                {priorMessages.map(m => (
                  <MessageBubble
                    key={m.id}
                    message={m}
                    sessionId={sessionId}
                    readOnly
                  />
                ))}
                <div
                  role="separator"
                  aria-label="End of previous session"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    margin: '6px 0 18px',
                    fontFamily: BODY_FONT,
                    fontSize: 12,
                    fontWeight: 600,
                    color: C.accentDark,
                    letterSpacing: '0.04em',
                  }}
                >
                  <span style={{ flex: 1, height: 1, background: `${C.accent}55` }} aria-hidden="true" />
                  <span style={{ whiteSpace: 'nowrap' }}>Continuing from your last session</span>
                  <span style={{ flex: 1, height: 1, background: `${C.accent}55` }} aria-hidden="true" />
                </div>
              </>
            )}

            {messages.map((m, i) => (
              <div key={m.id} className="chat-msg-anim" style={{ animationDelay: `${Math.min(i, 4) * 40}ms` }}>
                <MessageBubble message={m} sessionId={sessionId} />
              </div>
            ))}

            {sending && <TypingIndicator reduceMotion={reduceMotion} />}

            <div ref={messagesEndRef} />
          </div>
        </main>

        {/* Input bar */}
        <footer style={{
          flexShrink: 0,
          background: C.card,
          borderTop: `1px solid ${C.border}`,
          padding: '12px 14px 14px',
          boxShadow: '0 -2px 12px rgba(26, 31, 54, 0.04)',
        }}>
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            {(sendError || voiceError) && (
              <div
                role="alert"
                style={{
                  marginBottom: 10,
                  padding: '9px 14px',
                  background: C.errorBg,
                  border: `1.5px solid ${C.errorBorder}`,
                  borderRadius: 14,
                  color: C.errorText,
                  fontSize: 13.5,
                  fontWeight: 600,
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <span aria-hidden="true" style={{
                  width: 16, height: 16, borderRadius: '50%',
                  background: C.errorText, color: '#fff',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800, flexShrink: 0,
                }}>!</span>
                {sendError || voiceError}
              </div>
            )}

            {rateLimitNotice && (
              <div
                role="status"
                aria-live="polite"
                style={{
                  marginBottom: 10,
                  padding: '9px 14px',
                  background: C.warnBg,
                  border: `1.5px solid ${C.warnBorder}`,
                  borderRadius: 14,
                  color: C.warnText,
                  fontSize: 13.5,
                  fontWeight: 600,
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 15 }}>⏸️</span>
                {rateLimitNotice}
              </div>
            )}

            {/* Listening hint — shows while voice input is active */}
            {listening && !voiceError && (
              <div style={{
                marginBottom: 10,
                padding: '7px 12px',
                background: '#FEE2E2',
                border: '1.5px solid #FCA5A5',
                borderRadius: 12,
                color: '#B91C1C',
                fontSize: 13,
                fontWeight: 700,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 7,
                fontFamily: HEADING_FONT,
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: '#EF4444',
                  animation: 'chat-urgent-pulse 1s ease-in-out infinite',
                }} />
                I'm listening…
              </div>
            )}

            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 8,
            }}>
              <textarea
                ref={inputRef}
                className="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message…"
                rows={1}
                disabled={sending || endingSession}
                aria-label="Message Lantern"
              />
              {config?.voiceInputEnabled && (
                <button
                  type="button"
                  className={`chat-icon-btn ${listening ? 'chat-mic--listening' : ''}`}
                  onClick={toggleListening}
                  disabled={sending || endingSession}
                  aria-label={listening ? 'Stop listening' : 'Speak instead of typing'}
                  title={listening ? 'Stop listening' : 'Speak instead of typing'}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.8" />
                    <path d="M5 11a7 7 0 0 0 14 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    <path d="M12 18v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </button>
              )}
              <button
                type="button"
                className="chat-send"
                onClick={() => handleSend()}
                disabled={!input.trim() || sending || endingSession}
                aria-label="Send message"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <path d="M3 9h11M9 4l5 5-5 5" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>

            {/* Tiny safety footer — reminds the child the parent can see the chat */}
            <div style={{
              marginTop: 9,
              fontSize: 11,
              fontWeight: 600,
              color: C.textSubtle,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
              letterSpacing: '0.1px',
            }}>
              <span aria-hidden="true">🛡️</span>
              Your grown-up can see your chats
            </div>
            <div style={{
              marginTop: 4,
              fontSize: 11,
              fontWeight: 600,
              color: C.textSubtle,
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 5,
              letterSpacing: '0.1px',
            }}>
              {/* The emoji is its own flex item, so spacing comes from the
                  container's `gap` alone — the literal space that used to sit
                  before this span was being added on top of that gap, which is
                  what made the spacing look wrong (most visibly on iOS Safari).
                  `verticalAlign` was also removed: it has no effect on a flex
                  item, and the container's `alignItems: center` is what
                  actually centres the glyph against the text. */}
              <span>I'm an AI and I sometimes make mistakes. If something doesn't seem right, question it — that's great thinking!</span>
              <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>🧠</span>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
